import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from '../services/toastService'
import { useAuth } from '../context/AuthContext'
import { validateEmail, validatePassword, validatePasswordMatch } from '../utils/validators'
import { trackButtonClick } from '../utils/analytics'
import { markOnboardingStep } from '../components/onboarding/OnboardingProgress'
import { onboardingAPI } from '../services/api/onboarding'

/**
 * Custom hook for managing login and registration forms
 * Handles form state, validation, submission, and navigation
 */
export function useAuthForms(t, onSuccess = null, isImmersiveMode = false, onTabChange = null) {
  const navigate = useNavigate()
  const { login, register, googleAuth, isLoading: isAuthLoading } = useAuth()

  // Login form state
  const [loginFormData, setLoginFormData] = useState({ email: '', password: '' })
  const [loginErrors, setLoginErrors] = useState({})

  // Register form state
  const [registerFormData, setRegisterFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  })
  const [registerErrors, setRegisterErrors] = useState({})

  // Login form handlers
  const handleLoginChange = useCallback((e) => {
    const { name, value } = e.target
    setLoginFormData(prev => ({ ...prev, [name]: value }))
    // Clear error for this field when user starts typing
    if (loginErrors[name]) {
      setLoginErrors(prev => ({ ...prev, [name]: '' }))
    }
  }, [loginErrors])

  const validateLoginForm = useCallback(() => {
    const errors = {}
    
    if (!loginFormData.email) {
      errors.email = t('auth.emailRequired')
    } else if (!validateEmail(loginFormData.email, false)) {
      errors.email = t('auth.emailInvalid')
    }
    
    if (!loginFormData.password) {
      errors.password = t('auth.passwordRequired')
    }
    
    setLoginErrors(errors)
    return Object.keys(errors).length === 0
  }, [loginFormData, t])

  const handleLoginSubmit = useCallback(async (e) => {
    e.preventDefault()
    
    trackButtonClick('submit_login', 'login_tab', { is_immersive_mode: isImmersiveMode })
    
    if (!validateLoginForm()) return
    
    const result = await login({ email: loginFormData.email, password: loginFormData.password })
    
    if (result.success) {
      toast.success(t('auth.loginSuccess'))
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess()
      } else if (isImmersiveMode && onTabChange) {
        // In immersive mode, close the panel after successful login
        onTabChange(null, 'closed')
      } else {
        // Navigate to home in standalone mode
        navigate('/')
      }
    } else {
      toast.error(result.error || t('auth.loginFailed'))
    }
  }, [login, loginFormData, validateLoginForm, onSuccess, navigate, t, isImmersiveMode, onTabChange])

  // Register form handlers
  const handleRegisterChange = useCallback((e) => {
    const { name, value } = e.target
    setRegisterFormData(prev => ({ ...prev, [name]: value }))
    // Clear error for this field when user starts typing
    if (registerErrors[name]) {
      setRegisterErrors(prev => ({ ...prev, [name]: '' }))
    }
  }, [registerErrors])

  const validateRegisterForm = useCallback(() => {
    const errors = {}
    
    if (!registerFormData.firstName) {
      errors.firstName = t('auth.firstNameRequired')
    }
    
    if (!registerFormData.lastName) {
      errors.lastName = t('auth.lastNameRequired')
    }
    
    if (!registerFormData.email) {
      errors.email = t('auth.emailRequired')
    } else if (!validateEmail(registerFormData.email, false)) {
      errors.email = t('auth.emailInvalid')
    }
    
    const passwordValidation = validatePassword(registerFormData.password, 8)
    if (!passwordValidation.valid) {
      errors.password = passwordValidation.error
    }
    
    const passwordMatchValidation = validatePasswordMatch(registerFormData.password, registerFormData.confirmPassword)
    if (!passwordMatchValidation.valid) {
      errors.confirmPassword = passwordMatchValidation.error
    }
    
    setRegisterErrors(errors)
    return Object.keys(errors).length === 0
  }, [registerFormData, t])

  const handleRegisterSubmit = useCallback(async (e) => {
    e.preventDefault()
    
    trackButtonClick('submit_register', 'signup_tab', { is_immersive_mode: isImmersiveMode })
    
    if (!validateRegisterForm()) return
    
    const result = await register({
      firstName: registerFormData.firstName,
      lastName: registerFormData.lastName,
      email: registerFormData.email,
      password: registerFormData.password,
      referralCode: new URLSearchParams(window.location.search).get('ref') || undefined,
    })
    
    if (result.success) {
      toast.success(t('auth.registerSuccess'))
      const signedUpTs = Date.now()
      markOnboardingStep('signed_up', signedUpTs)
      onboardingAPI.patchSteps({ signed_up: signedUpTs }).catch(() => {})
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess()
      } else if (isImmersiveMode && onTabChange) {
        // Switch to login/profile tab in immersive mode
        onTabChange('auth')
      } else {
        // Navigate to home in standalone mode
        navigate('/')
      }
    } else {
      toast.error(result.error || t('auth.registerFailed'))
    }
  }, [register, registerFormData, validateRegisterForm, onSuccess, navigate, t, isImmersiveMode, onTabChange])

  return {
    // Login form
    loginFormData,
    loginErrors,
    handleLoginChange,
    validateLoginForm,
    handleLoginSubmit,
    
    // Register form
    registerFormData,
    registerErrors,
    handleRegisterChange,
    validateRegisterForm,
    handleRegisterSubmit,
    
    // Auth context
    isAuthLoading,
    googleAuth
  }
}
